import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Palette } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      await register(registerData);
      toast.success('Welcome to Artsy Craftsy!');
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-artsy-background flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-teal-600 rounded-full border-2 border-slate-900 shadow-sticker flex items-center justify-center">
              <Palette className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-slate-900">
              Artsy <span className="text-pink-500">Craftsy</span>
            </span>
          </Link>
        </div>

        {/* Register Card */}
        <div className="card-pricing">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2 text-center">Create Account</h1>
          <p className="font-body text-slate-600 text-center mb-8">Join our creative community</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="input-artsy"
                placeholder="Your full name"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-artsy"
                placeholder="your@email.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-artsy"
                placeholder="07XXX XXXXXX"
                required
                data-testid="register-phone"
              />
            </div>

            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-artsy pr-10"
                  placeholder="At least 6 characters"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Confirm Password</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="input-artsy"
                placeholder="Confirm your password"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="font-body text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 font-semibold hover:underline" data-testid="goto-login">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
